/**
 * GeographySwitcher — nav pill that shows the active region/locality and lets users switch.
 *
 * - Opens a dropdown listing all active regions with their localities.
 * - Persists choice via GeographyContext and navigates to /regions/:slug.
 * - Hidden on checkout, account, admin, and auth pages.
 */

import { useState } from 'react';
import { useLocation } from 'wouter';
import { useQuery } from '@tanstack/react-query';
import { ChevronDown, MapPin, Globe, Check, ChevronRight } from 'lucide-react';
import type { Region, Locality } from '@shared/schema';
import { getGeographyTheme } from '@shared/geography-theme';

import { fetchJson } from '@/lib/queryClient';
import { useGeographyContext } from '@/hooks/useGeographyContext';
import {
  buildScopedGeographyHref,
  formatGeographySlugLabel,
  resolveGeographySelectionFromUrl,
} from '@/lib/geography-navigation';
import { useTheme } from '@/hooks/useTheme';
import { clearPersistedGeography } from '@/lib/geography';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

const HIDDEN_PATHS = [
  '/checkout',
  '/account',
  '/admin',
  '/login',
  '/onboarding',
  '/creator-dashboard',
  '/venue-dashboard',
  '/class-dashboard',
];

export default function GeographySwitcher() {
  const [location, navigate] = useLocation();
  const { regionSlug, localitySlug, region, locality, setGeography } = useGeographyContext();
  const { theme } = useTheme();
  const [open, setOpen] = useState(false);
  const [expandedRegion, setExpandedRegion] = useState<string | null>(null);

  const { data: regions = [] } = useQuery<Region[]>({
    queryKey: ['active-regions'],
    queryFn: () => fetchJson<Region[]>('/api/regions'),
    staleTime: 1000 * 60 * 10,
  });

  const { data: expandedLocalities = [] } = useQuery<Locality[]>({
    queryKey: ['localities', expandedRegion],
    queryFn: () => fetchJson<Locality[]>(`/api/regions/${expandedRegion}/localities`),
    enabled: !!expandedRegion,
    staleTime: 1000 * 60 * 10,
  });

  const hidden = HIDDEN_PATHS.some(p => location.startsWith(p));
  if (hidden) return null;

  const search = typeof window === 'undefined' ? '' : window.location.search;
  const resolvedLocation = resolveGeographySelectionFromUrl(location, search, {
    regionSlug,
    localitySlug,
  });

  const activeScopeSlug = localitySlug ?? resolvedLocation.localitySlug ?? regionSlug;
  const geoTheme = getGeographyTheme(activeScopeSlug);
  const isDark = theme === 'dark';
  const activeColors = isDark ? geoTheme.colorsDark : geoTheme.colors;
  const primaryColor = activeColors.primary;
  const primaryFg = activeColors.primaryForeground;

  const isOnline = region?.type === 'online';
  const displayName =
    locality?.name ??
    (resolvedLocation.localitySlug
      ? formatGeographySlugLabel(resolvedLocation.localitySlug)
      : null) ??
    region?.name ??
    formatGeographySlugLabel(regionSlug);

  function handleSelectRegion(r: Region) {
    if (r.type === 'online') {
      setGeography(r.slug);
      setOpen(false);
      navigate(
        buildScopedGeographyHref(
          location,
          search,
          { regionSlug: r.slug },
          { regionSlug, localitySlug }
        )
      );
    } else {
      setExpandedRegion(prev => (prev === r.slug ? null : r.slug));
    }
  }

  function handleSelectLocality(r: Region, l: Locality) {
    setGeography(r.slug, l.slug);
    setOpen(false);
    navigate(
      buildScopedGeographyHref(
        location,
        search,
        { regionSlug: r.slug, localitySlug: l.slug },
        { regionSlug, localitySlug }
      )
    );
  }

  function handleSelectRegionWide(r: Region) {
    setGeography(r.slug);
    setOpen(false);
    navigate(
      buildScopedGeographyHref(
        location,
        search,
        { regionSlug: r.slug },
        { regionSlug, localitySlug }
      )
    );
  }

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="geography-switcher-trigger"
          className="flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-medium transition-all hover:opacity-90 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-1"
          style={{
            background: primaryColor,
            color: primaryFg,
            borderColor: primaryColor,
          }}
        >
          {isOnline ? (
            <Globe className="w-3.5 h-3.5 shrink-0" />
          ) : (
            <MapPin className="w-3.5 h-3.5 shrink-0" />
          )}
          <span className="max-w-[8.5rem] truncate leading-none">{displayName}</span>
          <ChevronDown
            className={`w-3.5 h-3.5 shrink-0 transition-transform duration-150 ${open ? 'rotate-180' : ''}`}
          />
        </button>
      </PopoverTrigger>

      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={36}
        avoidCollisions
        collisionPadding={{ top: 80, left: 8, right: 8, bottom: 8 }}
        className="w-60 p-0 rounded-xl border-border bg-popover shadow-xl overflow-hidden z-[200]"
      >
        <div className="px-3 py-2 border-b border-border">
          <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
            Select your area
          </p>
        </div>
        <ul className="py-1">
          {regions.map(r => {
            const rTheme = getGeographyTheme(r.slug);
            const isExpanded = expandedRegion === r.slug;
            const isActive = r.slug === regionSlug && !localitySlug;
            return (
              <li key={r.id}>
                <button
                  type="button"
                  onClick={() => handleSelectRegion(r)}
                  className="w-full flex items-center gap-3 px-3 py-2.5 text-sm text-left hover:bg-accent/10 transition-colors"
                >
                  <span
                    className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
                    style={{ background: rTheme.colors.primary }}
                  >
                    {r.type === 'online' ? (
                      <Globe className="w-3.5 h-3.5 text-white" />
                    ) : (
                      <MapPin className="w-3.5 h-3.5 text-white" />
                    )}
                  </span>
                  <span className="flex-1 font-medium text-foreground">{r.name}</span>
                  {isActive && <Check className="w-4 h-4 text-primary shrink-0" />}
                  {r.type === 'physical' && (
                    <ChevronRight
                      className={`w-3.5 h-3.5 text-muted-foreground transition-transform ${isExpanded ? 'rotate-90' : ''}`}
                    />
                  )}
                </button>
                {isExpanded && r.type === 'physical' && (
                  <div className="pl-10 pb-1">
                    <button
                      type="button"
                      onClick={() => handleSelectRegionWide(r)}
                      className="w-full text-left px-3 py-1.5 text-xs text-muted-foreground hover:text-foreground transition-colors"
                    >
                      {isActive ? (
                        <>
                          <Check className="inline w-3 h-3 mr-1 text-primary" />
                          All
                        </>
                      ) : (
                        'All'
                      )}
                    </button>
                    {expandedLocalities.map(l => {
                      const lActive = l.slug === localitySlug && r.slug === regionSlug;
                      return (
                        <button
                          key={l.id}
                          type="button"
                          onClick={() => handleSelectLocality(r, l)}
                          className="w-full text-left flex items-center gap-2 px-3 py-1.5 text-xs hover:bg-accent/10 transition-colors"
                        >
                          <span className="flex-1 text-foreground font-medium">{l.name}</span>
                          {lActive && <Check className="w-3 h-3 text-primary shrink-0" />}
                        </button>
                      );
                    })}
                  </div>
                )}
              </li>
            );
          })}
        </ul>
        <div className="px-3 py-2 border-t border-border flex flex-col gap-1.5">
          <button
            type="button"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors text-left"
            onClick={() => {
              clearPersistedGeography();
              setOpen(false);
              navigate('/');
            }}
          >
            ← All regions
          </button>
          <a
            href="/creator/apply"
            className="text-xs text-muted-foreground hover:text-foreground transition-colors"
            onClick={() => setOpen(false)}
          >
            Bring Kalovate to your area →
          </a>
        </div>
      </PopoverContent>
    </Popover>
  );
}
